/**
 * The contact directory (F7).
 *
 * One list for startup teams and customer people alike (PRD §5.7). The
 * organisation column carries the NAME rather than the type, because a
 * directory in which every row says only "STARTUP" tells a reader nothing
 * about who belongs to whom — the type is a filter, not an answer.
 *
 * Alphabetical by default, unlike the other lists. A directory is read by
 * looking someone up, and an order that shifts whenever anyone edits a record
 * cannot be scanned: you lose your place between visits.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.5, PRD §5.7, §5.9
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { ContactSummary } from '@nexus/contracts';

import { Badge } from '../../components/ui/Badge';
import type { Column } from '../../components/ui/data-table-types';
import { DataTable } from '../../components/ui/DataTable';
import { ExportButton } from '../../components/ui/ExportButton';
import { PageHead } from '../../components/ui/PageHead';
import { Pagination } from '../../components/ui/Pagination';
import { formatDate } from '../../lib/format';
import { contactPath } from '../../lib/routes';
import { useTableState } from '../../lib/use-table-state';

import { useContacts } from './api/contacts.queries';
import { ContactFilters } from './components/ContactFilters';

/** Sort fields the API allow-lists (see `CONTACT_SORT_FIELDS` on the server). */
const SORT_FIELDS = ['name', 'orgType', 'lastContactDate', 'createdAt', 'updatedAt'];

export function ContactsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, toggleSort, setPage, setPageSize, setFilter, clearFilters } =
    useTableState(SORT_FIELDS);

  const query = useMemo(
    () => ({
      page: state.page,
      pageSize: state.pageSize,
      ...state.filters,
      ...(state.sort === null
        ? {}
        : { sort: `${state.sort.field}:${state.sort.direction}` }),
    }),
    [state],
  );

  const { data, isPending, isError } = useContacts(query);

  const columns: readonly Column<ContactSummary>[] = useMemo(
    () => [
      {
        key: 'name',
        header: t('contact.columns.name'),
        cell: (row) => (
          <span className="flex items-center gap-2">
            <span>{row.name}</span>
            {/* Text, not a dot. PRD §9.7 forbids conveying status by colour
                alone, and the primary contact is the single most consequential
                flag in this table — it decides who gets called. */}
            {row.isPrimary ? <Badge tone="ok">{t('contact.primaryShort')}</Badge> : null}
          </span>
        ),
        sortField: 'name',
        card: 'primary',
      },
      {
        key: 'role',
        header: t('contact.columns.role'),
        cell: (row) => row.role ?? <span className="text-gray-400">—</span>,
        card: 'secondary',
      },
      {
        key: 'organisation',
        header: t('contact.columns.organisation'),
        /* The name of the startup or customer. The three unaffiliated types —
           INTERNAL, ADVISOR, INVESTOR — legitimately have neither, so the type
           itself is shown rather than a dash: "Internal" is the answer, not a
           missing value.

           The card layout lays its secondary cells out in one wrapping row
           separated only by a gap, so three short plain-text values run
           together as a phrase — "VP Sales HeatPump Ibérica Never". The
           organisation carries a leading separator on the card to break that
           up; it is hidden in the table, where the column edges already do
           the separating. */
        cell: (row) =>
          row.startupName ??
          row.customerName ?? (
            <span className="text-body">{t(`contact.orgType.${row.orgType}`)}</span>
          ),
        sortField: 'orgType',
        card: 'secondary',
      },
      {
        key: 'email',
        header: t('contact.columns.email'),
        /* A mailto, because the reason to look someone up in a directory is
           usually to contact them. `stopPropagation` so the link does not also
           trigger the row's navigation. */
        cell: (row) =>
          row.email === null ? (
            <span className="text-gray-400">—</span>
          ) : (
            <a
              href={`mailto:${row.email}`}
              className="text-blue hover:underline"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {row.email}
            </a>
          ),
        card: 'hidden',
      },
      {
        key: 'lastContact',
        header: t('contact.columns.lastContact'),
        /**
         * Prefixed with its own label.
         *
         * The card layout lays every secondary cell out in one wrapping row
         * separated only by a gap, which left three short values reading as a
         * single phrase: "VP Sales HeatPump Ibérica Never". A bare date or
         * "Never" is the one value here that says nothing about itself, so it
         * carries its label — the column header supplies that in the table,
         * and a card has no header to supply it.
         */
        cell: (row) => (
          <span className="inline-flex items-center gap-1.5">
            <span className="text-gray-400 md:hidden">
              {t('contact.columns.lastContactShort')}
            </span>
            {row.lastContactDate === null ? (
              <span className="text-gray-400">{t('contact.columns.never')}</span>
            ) : (
              <span className="tabular-nums">{formatDate(row.lastContactDate)}</span>
            )}
          </span>
        ),
        sortField: 'lastContactDate',
        numeric: true,
        card: 'secondary',
      },
    ],
    [t],
  );

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <>
      <PageHead
        kicker={t('nav.group.relationships')}
        title={t('contact.title')}
        {...(data === undefined
          ? {}
          : { lead: t('contact.count', { count: data.meta.total }) })}
      />

      <div className="mb-s3 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <ContactFilters
            filters={state.filters}
            onFilterChange={setFilter}
            onClear={clearFilters}
          />
        </div>
        {/* Exports the on-screen filters and sort; hidden from Viewers (F18, §4). */}
        <ExportButton
          entity="contacts"
          query={{
            ...state.filters,
            ...(state.sort === null
              ? {}
              : { sort: `${state.sort.field}:${state.sort.direction}` }),
          }}
        />
      </div>

      <DataTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        caption={t('contact.caption')}
        isLoading={isPending}
        sort={state.sort}
        onSortChange={toggleSort}
        emptyMessage={
          Object.keys(state.filters).length > 0
            ? t('contact.emptyFiltered')
            : t('contact.empty')
        }
        onRowSelect={(row) => {
          void navigate(contactPath(row.id));
        }}
      />

      {data === undefined ? null : (
        <Pagination
          page={data.meta.page}
          pageSize={data.meta.pageSize}
          total={data.meta.total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </>
  );
}
